export class EoEConfiguration {
  // Human-readable string storage
  private _macAddress: string = this.generateMacAddress()
  private _ipAddress: string = '0.0.0.0'
  private _subnetMask: string = '0.0.0.0'
  private _gateway: string = '0.0.0.0'
  private _dnsServer: string = '0.0.0.0'
  private _dnsName: string = ''

  // Manual flag overrides (if null, auto-calculated)
  private _macAddressIncluded: boolean | null = null
  private _ipAddressIncluded: boolean | null = null
  private _subnetMaskIncluded: boolean | null = null
  private _gatewayIncluded: boolean | null = null
  private _dnsServerIncluded: boolean | null = null
  private _dnsNameIncluded: boolean | null = null
  private _timestampIncluded: boolean = false // TwinCAT feature
  private _dhcpEnabled: boolean | null = null // Explicit DHCP flag (null = auto-detect)

  // Getters and setters for configuration values
  get macAddress(): string {
    return this._macAddress
  }
  set macAddress(value: string) {
    const processedValue = this.getValueOrDefault(value, this.generateMacAddress())
    if (!this.isValidMacAddress(processedValue)) {
      throw new Error(`Invalid MAC address format: ${processedValue}`)
    }
    this._macAddress = processedValue
  }

  get ipAddress(): string {
    return this._ipAddress
  }
  set ipAddress(value: string) {
    const processedValue = this.getValueOrDefault(value, '0.0.0.0')
    if (!this.isValidIpAddress(processedValue)) {
      throw new Error(`Invalid IP address format: ${processedValue}`)
    }
    this._ipAddress = processedValue
  }

  get subnetMask(): string {
    return this._subnetMask
  }
  set subnetMask(value: string) {
    const processedValue = this.getValueOrDefault(value, '0.0.0.0')
    if (!this.isValidIpAddress(processedValue)) {
      throw new Error(`Invalid subnet mask format: ${processedValue}`)
    }
    this._subnetMask = processedValue
  }

  get gateway(): string {
    return this._gateway
  }
  set gateway(value: string) {
    const processedValue = this.getValueOrDefault(value, '0.0.0.0')
    if (!this.isValidIpAddress(processedValue)) {
      throw new Error(`Invalid gateway address format: ${processedValue}`)
    }
    this._gateway = processedValue
  }

  get dnsServer(): string {
    return this._dnsServer
  }
  set dnsServer(value: string) {
    const processedValue = this.getValueOrDefault(value, '0.0.0.0')
    if (!this.isValidIpAddress(processedValue)) {
      throw new Error(`Invalid DNS server address format: ${processedValue}`)
    }
    this._dnsServer = processedValue
  }

  get dnsName(): string {
    return this._dnsName
  }
  set dnsName(value: string) {
    const processedValue = this.getValueOrDefault(value, '')
    if (processedValue.length > 31) {
      throw new Error(`DNS name too long (max 31 characters): ${processedValue}`)
    }
    this._dnsName = processedValue
  }

  // Flag control methods
  get macAddressIncluded(): boolean {
    return this._macAddressIncluded ?? this.isMacAddressNonZero()
  }
  set macAddressIncluded(value: boolean) {
    this._macAddressIncluded = value
  }

  get ipAddressIncluded(): boolean {
    return this._ipAddressIncluded ?? this.isIpAddressNonZero(this._ipAddress)
  }
  set ipAddressIncluded(value: boolean) {
    this._ipAddressIncluded = value
  }

  get subnetMaskIncluded(): boolean {
    return this._subnetMaskIncluded ?? this.isIpAddressNonZero(this._subnetMask)
  }
  set subnetMaskIncluded(value: boolean) {
    this._subnetMaskIncluded = value
  }

  get gatewayIncluded(): boolean {
    return this._gatewayIncluded ?? this.isIpAddressNonZero(this._gateway)
  }
  set gatewayIncluded(value: boolean) {
    this._gatewayIncluded = value
  }

  get dnsServerIncluded(): boolean {
    return this._dnsServerIncluded ?? this.isIpAddressNonZero(this._dnsServer)
  }
  set dnsServerIncluded(value: boolean) {
    this._dnsServerIncluded = value
  }

  get dnsNameIncluded(): boolean {
    return this._dnsNameIncluded ?? this._dnsName.length > 0
  }
  set dnsNameIncluded(value: boolean) {
    this._dnsNameIncluded = value
  }

  get timestampIncluded(): boolean {
    return this._timestampIncluded
  }
  set timestampIncluded(value: boolean) {
    this._timestampIncluded = value
  }

  // DHCP configuration detection and control
  get dhcp(): boolean {
    // If explicitly set, use that value
    if (this._dhcpEnabled !== null) {
      return this._dhcpEnabled
    }

    return (
      this._ipAddress === '0.0.0.0' &&
      this._subnetMask === '0.0.0.0' &&
      this._gateway === '0.0.0.0' &&
      this._dnsServer === '0.0.0.0' &&
      this._dnsName === ''
    )
  }
  set dhcp(value: boolean) {
    this._dhcpEnabled = value
    if (value) {
      this.configureDHCP() // Use current MAC address
    }
  }

  // Reset flag overrides to auto-calculation
  resetFlagsToAuto(): void {
    this._macAddressIncluded = null
    this._ipAddressIncluded = null
    this._subnetMaskIncluded = null
    this._gatewayIncluded = null
    this._dnsServerIncluded = null
    this._dnsNameIncluded = null
    this._dhcpEnabled = null
  }

  // Convenience methods for common configurations
  configureDHCP(macAddress?: string): void {
    if (macAddress) {
      this.macAddress = macAddress
    }
    // Keep the current MAC address if none provided
    this.ipAddress = '0.0.0.0'
    this.subnetMask = '0.0.0.0'
    this.gateway = '0.0.0.0'
    this.dnsServer = '0.0.0.0'
    this.dnsName = ''
    this.resetFlagsToAuto()
  }

  configureStatic(
    macAddress: string,
    ipAddress: string,
    subnetMask: string,
    gateway: string,
    dnsServer?: string,
    dnsName?: string,
  ): void {
    this.macAddress = macAddress
    this.ipAddress = ipAddress
    this.subnetMask = subnetMask
    this.gateway = gateway
    this.dnsServer = dnsServer || '0.0.0.0'
    this.dnsName = dnsName || ''
    this.resetFlagsToAuto()
  }

  // Parse from hex string
  static fromHexString(hexString: string): EoEConfiguration {
    // Remove spaces and ensure even length
    const cleanHex = hexString.replace(/\s/g, '')
    if (cleanHex.length !== 124) {
      // 62 bytes * 2 hex chars = 124
      throw new Error(`Invalid hex string length: expected 124 characters, got ${cleanHex.length}`)
    }

    const bytes = []
    for (let i = 0; i < cleanHex.length; i += 2) {
      bytes.push(parseInt(cleanHex.slice(i, i + 2), 16))
    }

    const config = new EoEConfiguration()

    // Skip header (bytes 0-3, should be 02 00 00 00)
    // Parse flags (bytes 4-7)
    const mainFlags = bytes[4]
    const timestampFlags = bytes[5]

    // Parse MAC address (bytes 8-13)
    const macBytes = bytes.slice(8, 14)
    config._macAddress = macBytes.map((b) => b.toString(16).padStart(2, '0')).join(':')

    // Parse IP address (bytes 14-17)
    const ipBytes = bytes.slice(14, 18).reverse()
    config._ipAddress = ipBytes.join('.')

    // Parse subnet mask (bytes 18-21)
    const subnetBytes = bytes.slice(18, 22).reverse()
    config._subnetMask = subnetBytes.join('.')

    // Parse gateway (bytes 22-25)
    const gatewayBytes = bytes.slice(22, 26).reverse()
    config._gateway = gatewayBytes.join('.')

    // Parse DNS server (bytes 26-29)
    const dnsBytes = bytes.slice(26, 30).reverse()
    config._dnsServer = dnsBytes.join('.')

    // Parse DNS name (bytes 30-61)
    const nameBytes = bytes.slice(30, 62)
    const nullIndex = nameBytes.indexOf(0)
    const nameLength = nullIndex >= 0 ? nullIndex : nameBytes.length
    config._dnsName = String.fromCharCode(...nameBytes.slice(0, nameLength))

    // Set flags based on parsed flags bytes
    config._macAddressIncluded = (mainFlags & 0x01) !== 0
    config._ipAddressIncluded = (mainFlags & 0x02) !== 0
    config._subnetMaskIncluded = (mainFlags & 0x04) !== 0
    config._gatewayIncluded = (mainFlags & 0x08) !== 0
    config._dnsServerIncluded = (mainFlags & 0x10) !== 0
    config._dnsNameIncluded = (mainFlags & 0x20) !== 0
    config._timestampIncluded = (timestampFlags & 0x01) !== 0

    return config
  }

  // Convert to hex string
  toHexString(): string {
    const bytes: number[] = []

    // Header (always 02 00 00 00)
    bytes.push(0x02, 0x00, 0x00, 0x00)

    // Flags (4 bytes total)
    let mainFlags = 0
    if (this.macAddressIncluded) mainFlags |= 0x01
    if (this.ipAddressIncluded) mainFlags |= 0x02
    if (this.subnetMaskIncluded) mainFlags |= 0x04
    if (this.gatewayIncluded) mainFlags |= 0x08
    if (this.dnsServerIncluded) mainFlags |= 0x10
    if (this.dnsNameIncluded) mainFlags |= 0x20

    let timestampFlags = 0
    if (this.timestampIncluded) timestampFlags |= 0x01

    bytes.push(mainFlags, timestampFlags, 0x00, 0x00)

    // MAC Address (6 bytes)
    const macBytes = this._macAddress.split(':').map((hex) => parseInt(hex, 16))
    bytes.push(...macBytes)

    // IP Address (4 bytes)
    const ipBytes = this._ipAddress
      .split('.')
      .map((num) => parseInt(num, 10))
      .reverse()
    bytes.push(...ipBytes)

    // Subnet Mask (4 bytes)
    const subnetBytes = this._subnetMask
      .split('.')
      .map((num) => parseInt(num, 10))
      .reverse()
    bytes.push(...subnetBytes)

    // Gateway (4 bytes)
    const gatewayBytes = this._gateway
      .split('.')
      .map((num) => parseInt(num, 10))
      .reverse()
    bytes.push(...gatewayBytes)

    // DNS Server (4 bytes)
    const dnsBytes = this._dnsServer
      .split('.')
      .map((num) => parseInt(num, 10))
      .reverse()
    bytes.push(...dnsBytes)

    // DNS Name (32 bytes)
    const nameBytes = new Array(32).fill(0)
    for (let i = 0; i < Math.min(this._dnsName.length, 31); i++) {
      nameBytes[i] = this._dnsName.charCodeAt(i)
    }
    bytes.push(...nameBytes)

    return bytes.map((b) => b.toString(16).padStart(2, '0')).join('')
  }

  // Get current flags as a byte
  get flagsByte(): number {
    let flags = 0
    if (this.macAddressIncluded) flags |= 0x01
    if (this.ipAddressIncluded) flags |= 0x02
    if (this.subnetMaskIncluded) flags |= 0x04
    if (this.gatewayIncluded) flags |= 0x08
    if (this.dnsServerIncluded) flags |= 0x10
    if (this.dnsNameIncluded) flags |= 0x20
    if (this.timestampIncluded) flags |= 0x40 // Use bit 6 for display purposes
    return flags
  }

  // Helper to handle null/undefined value resets (empty strings are still validated)
  private getValueOrDefault(value: string, defaultValue: string): string {
    return value.length === 0 ? defaultValue : value
  }

  // Generate a random MAC address for new configurations
  private generateMacAddress(): string {
    // Generate a locally administered MAC address (second bit of first octet set to 1)
    // This ensures it won't conflict with real hardware MAC addresses
    const bytes: number[] = []

    // First byte: set bit 1 (locally administered) and ensure bit 0 is 0 (unicast)
    bytes.push(0x02 | (Math.floor(Math.random() * 64) << 2))

    // Generate remaining 5 bytes randomly
    for (let i = 1; i < 6; i++) {
      bytes.push(Math.floor(Math.random() * 256))
    }

    return bytes.map((b) => b.toString(16).padStart(2, '0')).join(':')
  }

  // Validation helpers
  private isValidMacAddress(mac: string): boolean {
    const macRegex = /^([0-9A-Fa-f]{2}[:]){5}([0-9A-Fa-f]{2})$/
    return macRegex.test(mac)
  }

  private isValidIpAddress(ip: string): boolean {
    const parts = ip.split('.')
    if (parts.length !== 4) return false
    return parts.every((part) => {
      const num = parseInt(part, 10)
      return num >= 0 && num <= 255 && part === num.toString()
    })
  }

  private isMacAddressNonZero(): boolean {
    return this._macAddress !== '00:00:00:00:00:00' && this.isValidMacAddress(this._macAddress)
  }

  private isIpAddressNonZero(ip: string): boolean {
    return ip !== '0.0.0.0'
  }

  // Debug helper
  toString(): string {
    return `EoEConfiguration {
  MAC: ${this._macAddress} (included: ${this.macAddressIncluded})
  IP: ${this._ipAddress} (included: ${this.ipAddressIncluded})
  Subnet: ${this._subnetMask} (included: ${this.subnetMaskIncluded})
  Gateway: ${this._gateway} (included: ${this.gatewayIncluded})
  DNS: ${this._dnsServer} (included: ${this.dnsServerIncluded})
  Name: "${this._dnsName}" (included: ${this.dnsNameIncluded})
  Timestamp: ${this.timestampIncluded}
  Flags: 0x${this.flagsByte.toString(16).padStart(2, '0')}
}`
  }
}
